import { Avatar, IconButton, ListItem, ListItemAvatar, ListItemText, Typography, Divider, Grid, InputBase, List, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { withStyles } from "@mui/styles";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PublicIcon from "@mui/icons-material/Public";
import SearchIcon from "@mui/icons-material/Search";
import { StoresContext } from "contexts";
import React, { useContext, useState, useEffect, useRef } from "react";
import { observer as hooksObserver } from "mobx-react-lite";
import styles from "./PublicInvestigationListStyles";
import { fetchConnectDossierGeom, fetchConnectDossiers, fetchPublicInvestigationGeom, fetchPublicInvestigations, fetchPublicInvestigationsV2 } from "services/api";
import { useDebouncedCallback } from "use-debounce";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { formatDate } from "utils/date";
import { PUBLIC_INVESTIGATION_TYPES } from "stores/models/PublicInvestigationModel";

// @todo: after releasing v2, remove v1 deprecated code
const PublicInvestigationList = hooksObserver(({ classes }) => {
  const OMV_V2 = window.env.OMV_V2 === "true";
  const {
    mapStore: { setLatLng, center },
    applicationStore: { publicInvestigation: { setShape, type, connectRolType, setConnectRolType } },
  } = useContext(StoresContext);
  const isOtherDossiersFlow = type === PUBLIC_INVESTIGATION_TYPES.SEARCH_OTHER_DOSSIERS;

  const [publicInvestigations, setPublicInvestigations] = useState([]);
  const [searchItems, setSearchItems] = useState([]);
  const [search, setSearch] = useState("");
  const listRef = useRef(null);

  const [debouncedFetchPublicInvestigations] = useDebouncedCallback(async (searchTerm, rolTypeOverride) => {
    if (isOtherDossiersFlow) {
      const rolType = rolTypeOverride || connectRolType;
      const response = await fetchConnectDossiers({ searchTerm, rolType });
      const data = response?.data || response || [];
      const sorted = (Array.isArray(data) ? data : []).sort((a, b) => {
        const aDate = new Date(a.submissionDate || "");
        const bDate = new Date(b.submissionDate || "");
        const dateComparison = bDate.getTime() - aDate.getTime();
        const projectNumComparison = (a.projectNum || "").localeCompare(b.projectNum || "");
        return dateComparison !== 0 ? dateComparison : projectNumComparison;
      });
      setPublicInvestigations(sorted);
      return;
    }
    if (!OMV_V2) return;

    const { data } = await fetchPublicInvestigationsV2({ searchTerm });
    const sorted = (data || []).sort((a, b) => {
      const aDate = new Date(a.submissionDate || "");
      const bDate = new Date(b.submissionDate || "");
      const dateComparison = bDate.getTime() - aDate.getTime();
      const projectNumComparison = (a.projectNum || "").localeCompare(b.projectNum || "");
      return dateComparison !== 0 ? dateComparison : projectNumComparison;
    });
    setPublicInvestigations(sorted);
  }, 500);

  useEffect(() => {
    if (isOtherDossiersFlow) {
      setPublicInvestigations([]);
      listRef.current?.scrollTo(0);
      debouncedFetchPublicInvestigations(search, connectRolType);
      return;
    }
    if (OMV_V2) {
      debouncedFetchPublicInvestigations();
      return;
    }

    // Fallback version 1
    const getPublicInvestigations = async () => {
      const { data } = await fetchPublicInvestigations();
      setPublicInvestigations(data);
    };
    getPublicInvestigations();
  }, [isOtherDossiersFlow, connectRolType]);

  useEffect(() => {
    if (OMV_V2 || isOtherDossiersFlow) return;

    // Old version fallback
    if (publicInvestigations.length > 0) {
      const result = publicInvestigations.filter((investigation) => {
        if (investigation?.projectName.toLowerCase().indexOf(search.toLowerCase()) !== -1 || investigation?.projectNumber.toLowerCase().indexOf(search.toLowerCase()) !== -1) {
          return investigation;
        }
      });
      setSearchItems(result);
    } else {
      setSearchItems([]);
    }
  }, [search, publicInvestigations, OMV_V2, isOtherDossiersFlow]);

  const dossiersToShow = isOtherDossiersFlow || OMV_V2 ? publicInvestigations : searchItems;
  const searchPlaceholder = isOtherDossiersFlow ? "Zoeken in overige dossiers" : "Zoeken in omgevingsvergunningen";
  const introText = isOtherDossiersFlow
    ? "Zoek op referentie, dossiernummer of adres en selecteer het gewenste overig dossier om het op kaart weer te geven."
    : "Zoek op naam of projectnummer en selecteer de gewenste omgevingsvergunning uit onderstaande lijst om ze op kaart weer te geven.";

  return (
    <Grid container direction="column" justifyContent="flex-start" alignItems="stretch" className={classes.root}>
      <Grid item>
        <Typography variant="subtitle2" gutterBottom>
          {introText}
        </Typography>
        <Typography variant="caption" gutterBottom>
          Klik op Volgende om door te gaan.
        </Typography>
      </Grid>
      {isOtherDossiersFlow && (
        <Grid item className={classes.rolTypeSection}>
          <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            Dossiertype
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={connectRolType}
            onChange={(_, value) => {
              if (value) setConnectRolType(value);
            }}
            aria-label="Filter op RO of Milieu"
            className={classes.rolTypeToggle}
          >
            <ToggleButton value="ro" aria-label="Ruimtelijke ordening">
              Ruimtelijke ordening
            </ToggleButton>
            <ToggleButton value="milieu" aria-label="Milieu">
              Milieu
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}
      <Grid item>
        <div className={classes.searchWrapper}>
          <InputBase
            className={classes.input}
            placeholder={searchPlaceholder}
            inputProps={{ "aria-label": searchPlaceholder }}
            value={search}
            onChange={(e) => {
              const value = e.target.value || "";
              setSearch(value);
              if (isOtherDossiersFlow) {
                debouncedFetchPublicInvestigations(value, connectRolType);
              } else if (OMV_V2) {
                debouncedFetchPublicInvestigations(value.replace(/[OMV_]/g, ""));
              }
            }}
          />
          <Divider className={classes.divider} orientation="vertical" />
          <IconButton className={classes.iconButton} aria-label="search" color="primary">
            <SearchIcon />
          </IconButton>
        </div>
      </Grid>
      <Grid item className={classes.listContainer}>
        {dossiersToShow.length > 0 && (
          <List role="list" className={classes.list} dense>
            <AutoSizer>
              {({ width, height }) => (
                <>
                  <FixedSizeList
                    key={isOtherDossiersFlow ? connectRolType : "omv"}
                    ref={listRef}
                    height={height}
                    width={width}
                    itemSize={isOtherDossiersFlow || OMV_V2 ? 64 : 85}
                    itemCount={dossiersToShow.length}
                    overscanCount={5}
                  >
                    {ListItemRender({ dossiersToShow, OMV_V2, isOtherDossiersFlow, setLatLng, setShape, center })}
                  </FixedSizeList>
                </>
              )}
            </AutoSizer>
          </List>
        )}
      </Grid>
    </Grid>
  );
});

const ListItemRender =
  ({ dossiersToShow, OMV_V2, isOtherDossiersFlow, setLatLng, setShape, center }) =>
  ({ index, style }) => {
    const publicInvestigation = dossiersToShow[index];
    return (
      <ListItem
        key={index}
        style={style}
        button
        onClick={async () => {
          if (isOtherDossiersFlow) {
            const { projectNum, center: listCenter } = publicInvestigation;
            if (listCenter?.length === 2) {
              setLatLng({ lat: listCenter[1], lng: listCenter[0] });
            }
            const feature = await fetchConnectDossierGeom({ projectNum });
            if (feature) {
              const geometry = feature.geometry || feature;
              setShape(geometry);
              center({ type: "Feature", geometry, properties: {} });
            }
            return;
          }

          // New version when enabled
          if (OMV_V2) {
            const { projectNum, center: listCenter } = publicInvestigation;
            setLatLng({ lat: listCenter[1], lng: listCenter[0] });
            const feature = await fetchPublicInvestigationGeom({ projectNum });
            if (feature) setShape(feature.geometry);
            return;
          }

          // Fallback old version
          const {
            center: { coordinates },
            location,
          } = publicInvestigation;
          setLatLng({ lat: coordinates[1], lng: coordinates[0] });
          setShape(location);
        }}
      >
        <ListItemAvatar>
          <Avatar>
            <PublicIcon />
          </Avatar>
        </ListItemAvatar>
        {isOtherDossiersFlow ? (
          <ListItemText
            sx={{ overflow: "hidden", minWidth: 0 }}
            primary={publicInvestigation.primaryLabel || publicInvestigation.projectNum}
            secondary={publicInvestigation.secondaryLabel || publicInvestigation.projectNum}
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true }}
          />
        ) : OMV_V2 ? (
          <ListItemText primary={publicInvestigation.projectNum} secondary={formatDate(publicInvestigation.submissionDate)} />
        ) : (
          <ListItemText sx={{ maxWidth: 240 }} primary={publicInvestigation.projectNumber} secondary={publicInvestigation.projectName} />
        )}
        <IconButton size="small">
          <ArrowRightIcon />
        </IconButton>
      </ListItem>
    );
  };

export default withStyles(styles)(PublicInvestigationList);
